import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Router, RouterLink, RouterOutlet } from "@angular/router";
import { catchError, distinctUntilChanged, map, of, switchMap, tap } from "rxjs";

import { NavbarComponent } from "../../components/navbar/navbar.component";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatSelect, MatSelectTrigger } from "@angular/material/select";
import { toInteractiveTypedId } from "../../interactives/mappers/interactives-list.mapper";
import { InteractiveListItem } from "../../interactives/models/interactives-list.model";
import { InteractivesApiService } from "../../interactives/services/interactives.api.service";
import { NavbarService } from "./navbar.service";
import { ButtonComponent, ButtonVariant } from "@sxm-content-creators/tools-ds";
import { AuthService } from "../../auth/auth.service";
import { ThemesApiService } from "../../admin/themes/services/themes.api.service";
import { PwaBrandingService } from "../../shared/services/pwa-branding.service";
import { SnackbarService } from "../../components/snackbar/snackbar.service";

const MAX_SELECTED_FREEFORM_INTERACTIVES = 50;

@Component({
    selector: "app-tenant-interactive-layout",
    templateUrl: "./tenant-interactive-layout.component.html",
    styleUrl: "./tenant-interactive-layout.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NavbarComponent,
        RouterOutlet,
        RouterLink,
        MatFormField,
        MatLabel,
        MatOption,
        MatSelect,
        MatSelectTrigger,
        ButtonComponent
    ]
})
export class TenantInteractiveLayoutComponent implements OnDestroy {
    private readonly router = inject(Router);
    protected readonly navbarService = inject(NavbarService);
    private readonly auth = inject(AuthService);
    private readonly interactivesService = inject(InteractivesApiService);
    private readonly themesApi = inject(ThemesApiService);
    private readonly pwaBranding = inject(PwaBrandingService);
    private readonly snackbar = inject(SnackbarService);

    protected readonly canCreateInteractive = computed(() => this.auth.isAdmin() || this.auth.isProducer());

    constructor() {
        // The one layout with a single active tenant, and so a single theme to brand the
        // home-screen app with; every other route keeps the default branding.
        toObservable(this.navbarService.selectedTenant)
            .pipe(
                map(tenant => tenant?.themeId ?? null),
                distinctUntilChanged(),
                tap(() => this.navbarService.setSelectedTheme(null)),
                // catchError inside the switchMap, so one failed theme load doesn't end the stream.
                switchMap(themeId =>
                    themeId
                        ? this.themesApi.getTheme(themeId).pipe(
                              tap(theme => this.navbarService.setSelectedTheme(theme)),
                              map(theme => theme.themeConfig ?? null),
                              catchError(() => {
                                  this.snackbar.show("We couldn't load the theme.", "error");
                                  return of(null);
                              })
                          )
                        : of(null)
                ),
                takeUntilDestroyed()
            )
            .subscribe(config => this.pwaBranding.applyTheme(config));
    }

    ngOnDestroy(): void {
        // NavbarService's provider injector is cached by the route and outlives this component, so its
        // tenant/interactive selection must be cleared explicitly whenever this layout is torn down.
        // Otherwise, the next visit to this layout can start from a stale, previously-viewed tenant
        this.navbarService.reset();
        this.pwaBranding.reset();
    }

    protected readonly producerLink = computed((): string[] => {
        const typedId = this.navbarService.typedInteractiveId();
        return typedId ? ["/interactives/producer", typedId] : ["/interactives/producer"];
    });

    protected readonly hostLink = computed((): string[] => {
        const typedId = this.navbarService.typedInteractiveId();
        return typedId ? ["/interactives/host", typedId] : ["/interactives/host"];
    });

    // Custom mat-select-trigger showing just the interactive name(s), since the option content
    // also carries the type label, and mat-select's default trigger renders the option's full text.
    protected readonly selectedInteractivesLabel = computed(() => {
        const allInteractives = this.navbarService.allInteractives();
        const selectedIds = new Set(this.navbarService.selectedInteractiveIds());
        return allInteractives
            .filter(i => selectedIds.has(i.id))
            .map(i => i.name)
            .join(", ");
    });

    protected onTenantChange(tenantId: string): void {
        this.navbarService.setSelectedTenantId(tenantId);
        const view = this.navbarService.activeView()!;

        this.interactivesService
            .getAllInteractives(tenantId, true)
            .pipe(catchError(() => of({ items: [] })))
            .subscribe(({ items }) => {
                this.navbarService.setAllInteractives(items);
                if (!items.length) {
                    void this.router.navigate(["interactives", view], { replaceUrl: true });
                    return;
                }
                const firstInteractive = items[0];
                void this.router.navigate(["interactives", view, toInteractiveTypedId(firstInteractive)]);
            });
    }

    protected onInteractiveChange(value: string | string[]): void {
        const ids = Array.isArray(value) ? value : [value];
        const allInteractives = this.navbarService.allInteractives();
        const view = this.navbarService.activeView();

        if (this.navbarService.isMultiSelectInteractive()) {
            // Detect which item was just clicked by diffing against the previous selection.
            const previousIds = new Set(this.navbarService.selectedInteractiveIds());
            const addedId = ids.find(id => !previousIds.has(id));
            const added = addedId ? allInteractives.find(i => i.id === addedId) : null;

            // Clicked a poll: single-select behavior, navigate to poll route
            if (added?.isPoll) {
                void this.router.navigate(["interactives", view, toInteractiveTypedId(added)]);
                return;
            }

            // Deselected down to 1 with a different remaining interactive: update the URL anchor
            if (ids.length === 1 && ids[0] !== this.navbarService.interactiveId()) {
                const remaining = allInteractives.find(i => i.id === ids[0])!;
                void this.router.navigate(["interactives", view, toInteractiveTypedId(remaining)]);
                return;
            }

            // 2+ freeforms, or deselected down to 1 that already matches the URL anchor: update service state only
            this.navbarService.setSelectedInteractiveIds(ids);
            return;
        }

        // Single-select interactives: navigate to the selected interactive
        const interactive = allInteractives.find(i => i.id === ids[0])!;
        void this.router.navigate(["interactives", view, toInteractiveTypedId(interactive)]);
    }

    protected refresh(): void {
        this.navbarService.triggerRefresh();
    }

    /**
     * An option in the multi-select is disabled when either: it's the sole remaining selection,
     * or the selections are already at the multi-select limit {@link MAX_SELECTED_FREEFORM_INTERACTIVES}
     * and selecting more will exceed what freeform endpoints accept.
     */
    protected isInteractiveOptionDisabled(interactive: InteractiveListItem): boolean {
        const selectedIds = this.navbarService.selectedInteractiveIds();
        if (selectedIds.length === 1 && selectedIds[0] === interactive.id) return true;
        return selectedIds.length >= MAX_SELECTED_FREEFORM_INTERACTIVES && !selectedIds.includes(interactive.id);
    }

    /** "Poll" for a poll, or the slash-joined freeform submission types it accepts, e.g. "Voice/Text". */
    protected readonly interactiveTypeLabel = (interactive: InteractiveListItem): string => {
        if (interactive.isPoll) return "Poll";

        const types: string[] = [];
        if (interactive.isAudio) types.push("Voice");
        if (interactive.isText) types.push("Text");
        if (interactive.isVideo) types.push("Video");
        return types.join("/");
    };

    protected readonly ButtonVariant = ButtonVariant;
}
